import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Route, Truck, Package, ChevronDown, ChevronUp, ChevronRight, CheckCircle2 } from "lucide-react";
import { API } from "../../contexts/AuthContext";
import { Skeleton } from "../../components/ui/skeleton";
import { parseDateOnly } from "../../utils/format";

// BLOQUE 55 — Tarjeta compacta de itinerario para los dashboards de Jefe de
// Compras / Jefe de Logística y para la pantalla de Compras>Materiales de
// Residente de Obra / Operario Electricista (que no tienen Dashboard propio
// — aterrizan directo ahí). Siempre de solo lectura; el click deep-linkea al
// retiro/entrega correspondiente (mismo patrón del Bloque 34).
export function ItinerarioWidget({ linkTab = "retiro", collapsible = false, title = "Itinerario de la Semana" }) {
  const navigate = useNavigate();
  const [week, setWeek] = useState(null);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(collapsible);

  useEffect(() => {
    axios.get(`${API}/itinerary/week`, { withCredentials: true })
      .then(({ data }) => setWeek(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Skeleton className="h-24 w-full rounded-2xl" data-testid="itinerario-widget-skeleton" />;
  if (!week) return null;

  const todayDay = week.days.find(d => d.is_today);
  const todayStops = todayDay?.stops || [];
  const totalWeekStops = week.days.reduce((acc, d) => acc + d.stops.length, 0);

  const goTo = (numero) => navigate(`/compras/materiales?tab=${linkTab}&open=${encodeURIComponent(numero)}`);

  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-4 sm:p-5" data-testid="itinerario-widget">
      <button
        className="w-full flex items-center justify-between gap-2"
        onClick={() => collapsible && setCollapsed(v => !v)}
        data-testid="itinerario-widget-toggle"
      >
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded-lg bg-blue-50 border border-blue-100/80">
            <Route size={14} className="text-[#2E6DA4]" />
          </div>
          <h3 className="font-bold text-gray-900 text-[14px]">{title}</h3>
          {totalWeekStops > 0 && <span className="text-[11px] bg-amber-100 text-amber-700 font-bold px-2 py-0.5 rounded-full">{todayStops.length} hoy</span>}
        </div>
        {collapsible && (collapsed ? <ChevronDown size={16} className="text-gray-400" /> : <ChevronUp size={16} className="text-gray-400" />)}
      </button>

      {!collapsed && (
        <div className="mt-3 space-y-3">
          {/* Franja semanal — 7 días, hoy resaltado */}
          <div className="grid grid-cols-7 gap-1" data-testid="itinerario-widget-strip">
            {week.days.map(d => (
              <div key={d.fecha} className={`rounded-lg text-center py-1.5 ${d.is_today ? "bg-[#2E6DA4] text-white" : "bg-gray-50 text-gray-500"}`}>
                <p className="text-[9px] font-bold uppercase">{d.label.slice(0, 3)}</p>
                <p className="text-[11px] font-semibold">{parseDateOnly(d.fecha).getDate()}</p>
                {d.stops.length > 0 && <p className={`text-[9px] font-bold ${d.is_today ? "text-white/90" : "text-amber-600"}`}>{d.stops.length}</p>}
              </div>
            ))}
          </div>

          {/* Paradas de hoy */}
          {todayStops.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-5 text-gray-400">
              <CheckCircle2 size={18} className="text-emerald-300 mb-1" />
              <p className="text-xs font-medium">Sin paradas planificadas para hoy</p>
            </div>
          ) : (
            <div className="space-y-1.5" data-testid="itinerario-widget-today-list">
              {todayStops.map(stop => (
                <button
                  key={stop._id}
                  onClick={() => stop.document_available && goTo(stop.withdrawal_number)}
                  disabled={!stop.document_available}
                  data-testid={`itinerario-widget-row-${stop._id}`}
                  className={`w-full flex items-center justify-between gap-2 p-2.5 rounded-xl border text-left transition-all ${stop.cumplido ? "border-gray-100 bg-gray-50 opacity-60" : "border-gray-100 hover:border-[#2E6DA4]/30 hover:shadow-sm"}`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="w-5 h-5 rounded-full bg-gray-100 text-gray-600 text-[10px] font-bold flex items-center justify-center flex-shrink-0">{stop.orden}</span>
                    {stop.tipo === "retiro" ? <Truck size={12} className="text-sky-600 flex-shrink-0" /> : <Package size={12} className="text-emerald-600 flex-shrink-0" />}
                    <div className="min-w-0">
                      <p className="font-mono text-[11px] font-semibold text-gray-600">{stop.withdrawal_number || "—"}</p>
                      <p className="text-[12px] text-gray-800 truncate">{stop.tipo === "retiro" ? stop.provider_name : stop.project_name}{stop.sucursal ? ` · ${stop.sucursal}` : ""}</p>
                    </div>
                  </div>
                  {stop.cumplido ? <CheckCircle2 size={14} className="text-lime-500 flex-shrink-0" /> : <ChevronRight size={14} className="text-gray-300 flex-shrink-0" />}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
